import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { AuthStore } from '@application/auth.store';
import {
  Message,
  MessageKind,
  MessageSemanticType,
  PaymentStatus,
  PaymentRetryStatus,
  ReportStatus,
  ReportStatusUpdate,
  ReportSummary,
  ValidationStatus,
  ValidationUpdate
} from '@domain/models';
import { ChatRealtimePort, RealtimeConnectionState } from '@domain/ports';
import { generateId } from '@shared/id-generator';
import { httpUrl, wsUrl } from '@shared/runtime-config';

interface WebSocketTicket {
  ticket: string;
  expiresAt: number;
}

interface ServerFrame {
  type:
    | 'CONNECTED'
    | 'MESSAGE_ACK'
    | 'MESSAGE_PROCESSING'
    | 'BOT_MESSAGE'
    | 'PAYMENT_STATUS'
    | 'REPORT_STATUS'
    | 'ERROR';
  sessionId?: string;
  chatId?: string | number;
  messageId?: string;
  clientMessageId?: string;
  parentMessageId?: string;
  parentClientMessageId?: string;
  text?: string | null;
  paymentStatus?: PaymentStatus;
  paymentRetryStatus?: PaymentRetryStatus;
  reason?: string;
  code?: string;
  message?: string;
  operationNumber?: string;
  amount?: number;
  currency?: string;
  currencyAmount?: string;
  validationDays?: number;
  paymentDate?: string;
  traceId?: string;
  createdAt?: number;
  kind?: MessageKind;
  semanticType?: MessageSemanticType | null;
  mediaUrl?: string;
  caption?: string;
  validation?: PaymentStatus | ValidationStatus | null;
  validationReason?: string | null;
  reportStatus?: ReportStatus;
  reportSummary?: ReportSummary;
  reportUrl?: string;
}

type ClientContent =
  | { type: 'TEXT'; text: string }
  | { type: 'IMAGE'; mediaUrl: string };

interface ClientFrame {
  type: 'USER_MESSAGE';
  chatId: string | number;
  clientMessageId: string;
  content: ClientContent;
}

@Injectable({providedIn: 'root'})
export class WebSocketChatService extends ChatRealtimePort {
  private static readonly MAX_RECONNECT_ATTEMPTS = 5;
  private static readonly RECONNECT_BASE_DELAY_MS = 1000;
  private static readonly STABLE_CONNECTION_MS = 30000;

  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthStore);
  private readonly ticketUrl = `${httpUrl('whatsapp')}/api/v1/ws-ticket`;

  private socket: WebSocket | null = null;
  private connectPromise: Promise<void> | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stableConnectionTimer: ReturnType<typeof setTimeout> | null = null;
  private manuallyDisconnected = false;

  private readonly messageSubject = new Subject<Message>();
  private readonly validationSubject = new Subject<ValidationUpdate>();
  private readonly reportStatusSubject = new Subject<ReportStatusUpdate>();
  private readonly connectionSubject =
    new BehaviorSubject<RealtimeConnectionState>('disconnected');
  private readonly typingSubject = new BehaviorSubject<boolean>(false);

  override readonly incoming$: Observable<Message> = this.messageSubject.asObservable();
  override readonly validationUpdates$: Observable<ValidationUpdate> =
    this.validationSubject.asObservable();
  override readonly reportStatusUpdates$: Observable<ReportStatusUpdate> =
    this.reportStatusSubject.asObservable();
  override readonly connection$: Observable<RealtimeConnectionState> =
    this.connectionSubject.asObservable();
  override readonly typing$: Observable<boolean> = this.typingSubject.asObservable();

  connect(): void {
    if (!this.auth.currentUser()) {
      console.warn('[WhatsApp WS] Connection skipped: no authenticated user');
      return;
    }
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) {
      console.debug('[WhatsApp WS] Connection skipped: socket already active', {
        readyState: this.socket.readyState
      });
      return;
    }
    if (this.connectPromise) {
      console.debug('[WhatsApp WS] Connection skipped: ticket request already pending');
      return;
    }

    this.manuallyDisconnected = false;
    this.connectionSubject.next('connecting');
    console.info('[WhatsApp WS] Requesting connection ticket', {
      ticketUrl: this.ticketUrl
    });
    this.connectPromise = this.openSocket()
      .catch(error => this.handleConnectionFailure(error))
      .finally(() => {
        this.connectPromise = null;
      });
  }

  disconnect(): void {
    this.manuallyDisconnected = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearStableConnectionTimer();
    this.reconnectAttempts = 0;
    this.typingSubject.next(false);
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
    }
    this.connectionSubject.next('disconnected');
  }

  sendMessage(
    chatId: string | number,
    clientMessageId: string,
    text: string
  ): void {
    const userId = this.auth.currentUser()?.id;
    if (!userId) return;
    this.messageSubject.next({
      id: clientMessageId,
      chatId,
      userId,
      text,
      sender: 'user',
      kind: 'TEXT',
      clientMessageId,
      status: 'sending',
      createdAt: Date.now()
    });
    this.sendFrame({
      type: 'USER_MESSAGE',
      chatId,
      clientMessageId,
      content: {type: 'TEXT', text}
    });
    this.typingSubject.next(true);
  }

  sendImage(
    chatId: string | number,
    clientMessageId: string,
    mediaUrl: string
  ): void {
    const userId = this.auth.currentUser()?.id;
    if (!userId) return;
    this.messageSubject.next({
      id: clientMessageId,
      chatId,
      userId,
      text: mediaUrl,
      sender: 'user',
      kind: 'IMAGE',
      clientMessageId,
      mediaUrl,
      remoteMediaUrl: mediaUrl,
      validation: 'PENDING',
      status: 'sending',
      createdAt: Date.now()
    });
    this.sendFrame({
      type: 'USER_MESSAGE',
      chatId,
      clientMessageId,
      content: {type: 'IMAGE', mediaUrl}
    });
    this.typingSubject.next(true);
  }

  private async openSocket(): Promise<void> {
    const response = await firstValueFrom(
      this.http.post<WebSocketTicket>(this.ticketUrl, {})
    );
    console.info('[WhatsApp WS] Ticket received', {
      expiresAt: response.expiresAt
    });
    if (this.manuallyDisconnected) return;

    const endpoint = `${wsUrl('whatsappWs')}`;
    const url = `${endpoint}?ticket=${encodeURIComponent(response.ticket)}`;
    const ws = new WebSocket(url);
    let sessionEstablished = false;
    this.socket = ws;
    console.info('[WhatsApp WS] Socket created', {endpoint});
    ws.onopen = () => {
      console.info('[WhatsApp WS] Transport opened; waiting for CONNECTED frame', {
        endpoint
      });
    };
    ws.onmessage = (event: MessageEvent<string>) => {
      console.debug('[WhatsApp WS] Frame received', {
        type: this.frameType(event.data)
      });
      sessionEstablished = this.handleFrame(event.data) || sessionEstablished;
    };
    ws.onerror = () => {
      console.error('[WhatsApp WS] Browser reported a connection error', {
        endpoint,
        readyState: ws.readyState
      });
    };
    ws.onclose = event => {
      if (this.socket === ws) this.socket = null;
      this.clearStableConnectionTimer();
      this.connectionSubject.next('disconnected');
      if (!this.manuallyDisconnected) {
        console.warn('[WhatsApp WS] Connection closed', {
          endpoint,
          code: event.code,
          reason: event.reason || undefined,
          wasClean: event.wasClean,
          sessionEstablished
        });
      }
      if (sessionEstablished && this.isRetryableClose(event)) {
        this.scheduleReconnect();
      }
    };
  }

  private handleConnectionFailure(error: unknown): void {
    this.connectionSubject.next('disconnected');
    console.error('[WhatsApp WS] Ticket request failed', {
      status: error instanceof HttpErrorResponse ? error.status : undefined,
      message: error instanceof Error ? error.message : String(error)
    });
    if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
      return;
    }
    this.scheduleReconnect();
  }

  private sendFrame(frame: ClientFrame): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(frame));
  }

  private handleFrame(raw: string): boolean {
    let frame: ServerFrame;
    try {
      frame = JSON.parse(raw) as ServerFrame;
    } catch {
      return false;
    }

    switch (frame.type) {
      case 'CONNECTED':
        console.info('[WhatsApp WS] Session established', {
          sessionId: frame.sessionId
        });
        this.connectionSubject.next('connected');
        this.scheduleStableConnectionReset();
        return true;
      case 'MESSAGE_ACK':
        this.emitUserMessageUpdate(frame, 'sent');
        return false;
      case 'MESSAGE_PROCESSING':
        this.emitUserMessageUpdate(frame, 'processing');
        return false;
      case 'BOT_MESSAGE':
        this.emitBotMessage(frame);
        return false;
      case 'PAYMENT_STATUS':
        this.emitPaymentStatus(frame);
        return false;
      case 'REPORT_STATUS':
        if (frame.chatId != null && frame.messageId && frame.reportStatus) {
          this.reportStatusSubject.next({chatId: frame.chatId,
            messageId: frame.messageId, reportStatus: frame.reportStatus});
        }
        return false;
      case 'ERROR':
        this.emitError(frame);
        return false;
    }
  }

  private emitUserMessageUpdate(
    frame: ServerFrame,
    status: Message['status']
  ): void {
    if (frame.chatId == null || !frame.clientMessageId) return;
    this.messageSubject.next({
      id: frame.messageId ?? frame.clientMessageId,
      chatId: frame.chatId,
      userId: this.auth.currentUser()?.id ?? '',
      text: '',
      sender: 'user',
      createdAt: frame.createdAt,
      clientMessageId: frame.clientMessageId,
      messageId: frame.messageId,
      status
    });
  }

  private emitBotMessage(frame: ServerFrame): void {
    if (frame.chatId == null || (!frame.text && !frame.mediaUrl
      && frame.semanticType !== 'REPORT_STATUS')) return;
    const paymentStatus = this.asPaymentStatus(frame.validation);
    this.typingSubject.next(false);
    this.messageSubject.next({
      id: frame.messageId ?? generateId(),
      chatId: frame.chatId,
      userId: 'bot',
      text: frame.text ?? '',
      sender: 'bot',
      createdAt: frame.createdAt,
      kind: frame.kind ?? (frame.mediaUrl ? 'IMAGE' : 'TEXT'),
      semanticType: frame.semanticType ?? null,
      messageId: frame.messageId,
      mediaUrl: frame.mediaUrl,
      caption: frame.caption,
      validation: paymentStatus
        ? this.mapPaymentToValidationStatus(paymentStatus)
        : frame.validation ?? undefined,
      paymentStatus: paymentStatus ?? undefined,
      paymentRetryStatus: frame.paymentRetryStatus,
      validationReason: frame.validationReason ?? undefined,
      operationNumber: frame.operationNumber,
      currencyAmount: frame.currencyAmount,
      validationDays: frame.validationDays,
      paymentDate: frame.paymentDate,
      reportStatus: frame.reportStatus,
      reportSummary: frame.reportSummary,
      reportUrl: frame.reportUrl,
      parentClientMessageId: frame.parentClientMessageId,
      parentMessageId: frame.parentMessageId,
      status: 'done'
    });
  }

  private emitPaymentStatus(frame: ServerFrame): void {
    if (frame.chatId == null || !frame.paymentStatus) return;
    this.validationSubject.next({
      messageId: frame.messageId,
      clientMessageId: frame.clientMessageId,
      createdAt: frame.createdAt,
      status: this.mapPaymentToValidationStatus(frame.paymentStatus),
      paymentStatus: frame.paymentStatus,
      reason: frame.reason
    });
  }

  private emitError(frame: ServerFrame): void {
    if (frame.chatId == null) return;
    this.typingSubject.next(false);
    const text = this.errorText(frame.code, frame.message);
    if (frame.clientMessageId || frame.parentMessageId) {
      this.messageSubject.next({
        id: frame.parentMessageId ?? frame.clientMessageId ?? generateId(),
        chatId: frame.chatId,
        userId: this.auth.currentUser()?.id ?? '',
        text: '',
        sender: 'user',
        createdAt: frame.createdAt,
        clientMessageId: frame.clientMessageId,
        messageId: frame.parentMessageId,
        status: 'failed',
        validation: 'ERROR',
        validationReason: text
      });
      return;
    }
    this.messageSubject.next({
      id: generateId(),
      chatId: frame.chatId,
      userId: 'bot',
      text,
      sender: 'bot',
      createdAt: frame.createdAt,
      kind: 'TEXT',
      status: 'failed'
    });
  }

  private mapPaymentToValidationStatus(status: PaymentStatus): ValidationStatus {
    switch (status) {
      case 'VALIDATED':
        return 'OK';
      case 'ERROR_AGENT':
      case 'ERROR_PROCESSOR':
        return 'ERROR';
      default:
        return 'REJECTED';
    }
  }

  private asPaymentStatus(value: unknown): PaymentStatus | null {
    const statuses: PaymentStatus[] = [
      'VALIDATED',
      'DUPLICATED',
      'NOT_FOUND',
      'DATE_EXPIRED',
      'NON_PAYMENT_IMAGE',
      'CHANNEL_NOT_ALLOWED',
      'MANUAL_REVIEW_REQUIRED',
      'USER_CONFIRMATION_REQUIRED',
      'ERROR_AGENT',
      'ERROR_PROCESSOR'
    ];
    return statuses.includes(value as PaymentStatus) ? value as PaymentStatus : null;
  }

  private errorText(code: string | undefined, message: string | undefined): string {
    switch (code) {
      case 'BULKHEAD_FULL':
        return 'Demasiadas imágenes en proceso, intenta en unos segundos.';
      case 'IMAGE_VALIDATION_FAILED':
        return 'No se pudo enviar la imagen a validación.';
      case 'AGENT_TIMEOUT':
        return 'El asistente no respondió a tiempo.';
      case 'AGENT_UNAVAILABLE':
        return 'Servicio no disponible, intenta más tarde.';
      case 'INVALID_MESSAGE':
        return message ?? 'Mensaje inválido.';
      default:
        return 'Ocurrió un error procesando tu solicitud.';
    }
  }

  private scheduleReconnect(): void {
    if (
      this.manuallyDisconnected
      || !this.auth.currentUser()
      || this.reconnectTimer
      || this.reconnectAttempts >= WebSocketChatService.MAX_RECONNECT_ATTEMPTS
    ) {
      return;
    }
    this.reconnectAttempts++;
    const delay = WebSocketChatService.RECONNECT_BASE_DELAY_MS
      * Math.pow(2, this.reconnectAttempts - 1);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private isRetryableClose(event: CloseEvent): boolean {
    return !this.manuallyDisconnected
      && event.code !== 1000
      && event.code !== 1008
      && event.code !== 4401
      && event.code !== 4403;
  }

  private scheduleStableConnectionReset(): void {
    this.clearStableConnectionTimer();
    this.stableConnectionTimer = setTimeout(() => {
      this.stableConnectionTimer = null;
      this.reconnectAttempts = 0;
    }, WebSocketChatService.STABLE_CONNECTION_MS);
  }

  private clearStableConnectionTimer(): void {
    if (!this.stableConnectionTimer) return;
    clearTimeout(this.stableConnectionTimer);
    this.stableConnectionTimer = null;
  }

  private frameType(raw: string): string {
    try {
      const frame = JSON.parse(raw) as {type?: unknown};
      return typeof frame.type === 'string' ? frame.type : 'UNKNOWN';
    } catch {
      return 'INVALID_JSON';
    }
  }
}
